import {
  Button,
  Dialog,
  DialogTrigger,
  Heading,
  Modal,
  ModalOverlay,
  OverlayTriggerStateContext,
} from 'react-aria-components'

import BuyAgainItemGroups from '@/components/BuyAgainItemGroups/BuyAgainItemGroups'
import {getVariationFromProductDetails} from '@/components/ProductCard/ProductCardActions'
import useAddToOrRemoveFromCartMutation from '@/lib/cart/hooks/useAddToOrRemoveFromCartMutation'
import {useLocale} from '@/lib/locale/locale'
import {useSetCustomItems} from '@/lib/payment/store'
import type {CartItem} from '@/models/cartModel'
import {type Order} from '@/models/orderModel'
import {PaymentRoute} from '@/routeRegistry'
import {getMinimalMultiProductDetails} from '@/services/productService'
import toastErrorMessage from '@/utils/errors/toastErrorMessage'

import style from './ReturnDialog.module.scss'

interface Props {
  order: Order
  preSelectedItemKey?: string
  onOpenChange?: (isOpen: boolean) => void
}

const BuyAgainDialog = memo<Props>(function BuyAgainDialog({order, preSelectedItemKey}) {
  const [locale] = useLocale()
  const [selection, setSelection] = useMutative(new Map<string, boolean>())
  const latestSelection = useLatest(selection)
  const [quantities, setQuantities] = useMutative(new Map<string, number>())
  const latestQuantities = useLatest(quantities)
  const [isBuying, setIsBuying] = useState(false)

  useEffect(
    function initializePreSelection() {
      if (preSelectedItemKey) {
        setSelection(draft => {
          draft.set(preSelectedItemKey, true)
        })
      }
    },
    [preSelectedItemKey, setSelection],
  )

  const {mutateAsync: addToOrRemoveFromCart, status: addToOrRemoveFromCartStatus} =
    useAddToOrRemoveFromCartMutation()

  const state = use(OverlayTriggerStateContext)

  const oldProducts = order.lineItems

  const {
    data: newProducts = [],
    // refetch,
  } = useQuery({
    queryKey: ['order', 'products', order.lineItems, locale],
    queryFn: async ({signal}) => {
      const results = await getMinimalMultiProductDetails({
        locale,
        signal,
        products: order.lineItems,
      })

      if (!results) return []

      // Initialize quantities map with original order quantities
      order.lineItems.forEach(item => {
        quantities.set(`${item.retailer}-${item.id}`, item.quantity)
      })

      return results.products
        .map((result, index) => {
          const price = result.price
          if (typeof price === 'string') return false
          const variation = getVariationFromProductDetails(result, result)
          return {
            ...order.lineItems[index],
            ...result,
            price,
            quantity: 1,
            variation: variation ? Object.fromEntries(variation) : undefined,
          } satisfies CartItem
        })
        .filter(item => !!item)
    },
    enabled: !!state?.isOpen,
  })

  const products = useMemo<(CartItem & {isAvailable?: boolean})[]>(() => {
    // Create a map of new products for O(1) lookup
    const newProductsMap = new Map(
      newProducts.map(product => [`${product.retailer}-${product.id}`, product]),
    )

    // Map through old products and either use new product data or mark as unavailable
    return oldProducts.map(oldProduct => {
      const key = `${oldProduct.retailer}-${oldProduct.id}`
      const newProduct = newProductsMap.get(key)

      if (newProduct) {
        return {...newProduct, isAvailable: true}
      }

      return {
        ...oldProduct,
        isAvailable: false,
      }
    })
  }, [oldProducts, newProducts])

  const latestProducts = useLatest(products)
  const navigate = useNavigate()
  const setCustomItems = useSetCustomItems()

  const onBuyNow = useCallback(
    async (callback?: () => void) => {
      const itemsToBuy = Array.from(latestSelection.current.entries())
        .filter(([, value]) => value)
        .map(([key]) => key)

      if (!itemsToBuy.length) {
        toastErrorMessage('Please select at least one item to buy.')
        return
      }

      setIsBuying(true)

      const selectedItems = latestProducts.current
        .filter((item): item is CartItem => {
          if (!item.isAvailable) return false
          const key = `${item.retailer}-${item.id}`
          return itemsToBuy.includes(key)
        })
        .map(item => ({
          ...item,
          quantity: latestQuantities.current.get(`${item.retailer}-${item.id}`) ?? 1,
        }))

      setCustomItems(selectedItems)

      setIsBuying(false)

      callback?.()

      await navigate({
        to: PaymentRoute.fullPath,
        search: {
          custom: true,
        },
      })
    },
    [navigate, setCustomItems],
  )

  const onAddToCart = useCallback(
    (callback?: () => void) => {
      const itemsToBuy = Array.from(latestSelection.current.entries())
        .filter(([, value]) => value)
        .map(([key]) => key)

      if (!itemsToBuy.length) {
        toastErrorMessage('Please select at least one item to add to cart.')
        return
      }

      const selectedItems = latestProducts.current
        .filter((item): item is CartItem => {
          if ('isAvailable' in item && !item.isAvailable) return false
          const key = `${item.retailer}-${item.id}`
          return itemsToBuy.includes(key)
        })
        .map(item => ({
          ...item,
          quantity: latestQuantities.current.get(`${item.retailer}-${item.id}`) ?? 1,
        }))

      addToOrRemoveFromCart({
        items: selectedItems,
      })
        .then(() => {
          callback?.()
        })
        .catch(() => {
          /* empty */
        })
    },
    [addToOrRemoveFromCart],
  )

  const onSelect = useCallback((item: CartItem, state: boolean) => {
    setSelection(draft => {
      draft.set(`${item.retailer}-${item.id}`, state)
    })
  }, [])

  const onChangeQuantity = useCallback((item: CartItem, quantity: number) => {
    setQuantities(draft => {
      draft.set(`${item.retailer}-${item.id}`, quantity)
    })
  }, [])
  return (
    <ModalOverlay
      className={({isEntering, isExiting}) =>
        clsx(style.Overlay, isEntering && style.IsEntering, isExiting && style.IsExiting)
      }
      isDismissable
    >
      <Modal className={style.Modal}>
        <Dialog className={style.Dialog}>
          {({close}) => {
            return (
              <>
                <Heading className={style.Heading} slot='title'>
                  Select items to buy again
                </Heading>
                <div className={style.Content}>
                  {products.length > 0 ? (
                    <BuyAgainItemGroups
                      onSelect={onSelect}
                      selection={selection}
                      onQuantityChange={onChangeQuantity}
                      quantities={quantities}
                      items={products}
                    />
                  ) : (
                    <div className={style.NoProduct}>No product available to buy again.</div>
                  )}
                </div>
                <div className={style.Action}>
                  <Button className={style.Cancel} onPress={close}>
                    {products.length > 0 ? 'Cancel' : 'Close'}
                  </Button>
                  {products.length > 0 && (
                    <div className={style.ButtonGroup}>
                      <Button
                        className={style.Ok}
                        onPress={() => {
                          onAddToCart(close)
                        }}
                        isDisabled={addToOrRemoveFromCartStatus === 'pending'}
                      >
                        {addToOrRemoveFromCartStatus === 'pending' ? 'Adding...' : 'Add to cart'}
                      </Button>
                      <Button
                        className={style.Ok}
                        onPress={() => {
                          void onBuyNow(close)
                        }}
                        isDisabled={isBuying}
                      >
                        {isBuying ? 'Buying...' : 'Buy Now'}
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )
          }}
        </Dialog>
      </Modal>
    </ModalOverlay>
  )
})

const BuyAgainDialogWrapper = deepMemo<PropsWithChildren<Props>>(function BuyAgainDialogWrapper({
  children,
  order,
  preSelectedItemKey,
  onOpenChange,
}) {
  return (
    <DialogTrigger onOpenChange={onOpenChange}>
      {children}
      <BuyAgainDialog order={order} preSelectedItemKey={preSelectedItemKey} />
    </DialogTrigger>
  )
})()

export default BuyAgainDialogWrapper
